import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Save } from "lucide-react";
import { clientAPI } from "@/lib/client-api";
import type { BookSection } from "@/lib/db-api";

const AdminBookSection = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [section, setSection] = useState<BookSection | null>(null);

  useEffect(() => {
    const auth = localStorage.getItem('adminAuth');
    if (auth !== 'true') {
      navigate('/admin/login');
      return;
    }
    loadSection();
  }, [navigate]);

  const loadSection = () => {
    try {
      const data = clientAPI.getBookSection();
      if (data) setSection(data);
    } catch (error) {
      console.error('Error loading book section:', error);
    }
  };

  const handleSave = () => {
    if (!section) return;

    try {
      clientAPI.updateBookSection(section);
      toast({
        title: "Success",
        description: "Contact information updated successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update contact information",
        variant: "destructive",
      });
    }
  };

  if (!section) return null;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="container mx-auto px-4 py-4">
          <Button onClick={() => navigate('/admin/dashboard')} variant="ghost">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Dashboard
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-3xl">
        <Card>
          <CardHeader>
            <CardTitle>Edit Contact Information</CardTitle>
            <CardDescription>Update the booking section and contact details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={section.title}
                onChange={(e) => setSection({ ...section, title: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={section.description}
                onChange={(e) => setSection({ ...section, description: e.target.value })}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={section.email}
                onChange={(e) => setSection({ ...section, email: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={section.phone}
                onChange={(e) => setSection({ ...section, phone: e.target.value })}
              />
            </div>
            <Button onClick={handleSave}>
              <Save className="mr-2 h-4 w-4" />
              Save Changes
            </Button>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdminBookSection;
